// AI SDR Module Index
import React, { useState, useContext } from "react";
import { Routes, Route, Navigate, Link, useLocation } from "react-router-dom";
import { AuthContext } from "../AuthProvider";
import TeamAccessWrapper from "./components/TeamAccessWrapper";
import Sidebar from "./components/Sidebar";

// Import all components
import Dashboard from "./Dashboard";
import CampaignBuilder from "./CampaignBuilder";
import Campaigns from "./Campaigns";
import VoiceMail from "./VoiceMail";
import RealTimeVoice from "./RealTimeVoice";
import RealTimeSMS from "./RealTimeSMS";
import Leads from "./Leads";
import Personas from "./Personas";
import Settings from "./Settings";
import Companies from "./Companies";
import BlacklistChecker from "./components/BlacklistChecker";
import GeneratorChecker from "./components/GeneratorChecker";
import Generators from "./components/Generators";
import Collectors from "./components/Collectors";
import CollectorResearch from "./components/CollectorResearch";
import ResearchViewer from "./components/ResearchViewer";
import AccessCodePopup from "./components/AccessCodePopup";
import Documentation from "./Documentation";
import AgentOrchestration from "./AgentOrchestration";

// Dashboard component with sidebar
const DashboardLayout = ({
  selectedTeam,
  teams,
  clearTeamSelection,
  switchToTeam,
  children,
  isIframe = false,
}) => {
  const location = useLocation();

  // Always show sidebar - removed iframe condition for hiding sidebar
  return (
    <div className="flex h-screen bg-gray-100">
      {/* Use the dedicated Sidebar component */}
      <Sidebar
        selectedTeam={selectedTeam}
        teams={teams}
        clearTeamSelection={clearTeamSelection}
        switchToTeam={switchToTeam}
      />

      {/* Main Content */}
      <div className="flex-1 overflow-hidden">
        <div className="h-full overflow-y-auto">
          {children ||
            (location.pathname === "/ai-sdr/" ? (
              <Dashboard selectedTeam={selectedTeam} isIframe={isIframe} />
            ) : (
              <div className="p-6">
                <h2>Select a module from the sidebar</h2>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
};

// Main AI-SDR component with centralized team access and access code protection
const AiSdr = () => {
  const { user } = useContext(AuthContext);
  const location = useLocation();
  const [debugEnabled, setDebugEnabled] = useState(false);
  const [hasAccess, setHasAccess] = useState(false);

  // Check for iframe parameter
  const searchParams = new URLSearchParams(location.search);
  const isIframe = searchParams.get("iframe") === "true";

  // Check if access has been granted previously
  React.useEffect(() => {
    const storedAccess = localStorage.getItem("ai-sdr-access");
    if (storedAccess === "granted") {
      setHasAccess(true);
    }
  }, []);

  const handleAccessGranted = () => {
    setHasAccess(true);
  };

  // Helper function to preserve query parameters in redirects
  const preserveQueryParams = (path) => {
    const currentSearch = location.search;
    return `${path}${currentSearch}`;
  };

  // Redirect to /ai-sdr/ if on exact /ai-sdr
  if (location.pathname === "/ai-sdr") {
    return <Navigate to={preserveQueryParams("/ai-sdr/")} replace />;
  }

  // If no access yet, show the access code popup
  if (!hasAccess) {
    return <AccessCodePopup onAccess={handleAccessGranted} />;
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <Routes>
        {/* Main dashboard */}
        <Route
          path="/"
          element={
            <TeamAccessWrapper
              moduleName="AI-SDR Dashboard"
              showDebugToggle={false}
            >
              {({ selectedTeam, teams, clearTeamSelection, switchToTeam }) => (
                <DashboardLayout
                  selectedTeam={selectedTeam}
                  teams={teams}
                  clearTeamSelection={clearTeamSelection}
                  switchToTeam={switchToTeam}
                  isIframe={isIframe}
                >
                  <Dashboard selectedTeam={selectedTeam} isIframe={isIframe} />
                </DashboardLayout>
              )}
            </TeamAccessWrapper>
          }
        />

        {/* Campaign Builder */}
        <Route
          path="/campaign-builder"
          element={
            <TeamAccessWrapper
              moduleName="Campaign Builder"
              showDebugToggle={false}
            >
              {({ selectedTeam, teams, clearTeamSelection, switchToTeam }) => (
                <DashboardLayout
                  selectedTeam={selectedTeam}
                  teams={teams}
                  clearTeamSelection={clearTeamSelection}
                  switchToTeam={switchToTeam}
                  isIframe={isIframe}
                >
                  <CampaignBuilder />
                </DashboardLayout>
              )}
            </TeamAccessWrapper>
          }
        />

        {/* Campaign Builder with ID */}
        <Route
          path="/campaign-builder/:campaignId"
          element={
            <TeamAccessWrapper
              moduleName="Campaign Builder"
              showDebugToggle={false}
            >
              {({ selectedTeam, teams, clearTeamSelection, switchToTeam }) => (
                <DashboardLayout
                  selectedTeam={selectedTeam}
                  teams={teams}
                  clearTeamSelection={clearTeamSelection}
                  switchToTeam={switchToTeam}
                  isIframe={isIframe}
                >
                  <CampaignBuilder />
                </DashboardLayout>
              )}
            </TeamAccessWrapper>
          }
        />

        {/* Campaigns */}
        <Route
          path="/campaigns"
          element={
            <TeamAccessWrapper moduleName="Campaigns" showDebugToggle={false}>
              {({ selectedTeam, teams, clearTeamSelection, switchToTeam }) => (
                <DashboardLayout
                  selectedTeam={selectedTeam}
                  teams={teams}
                  clearTeamSelection={clearTeamSelection}
                  switchToTeam={switchToTeam}
                  isIframe={isIframe}
                >
                  <Campaigns />
                </DashboardLayout>
              )}
            </TeamAccessWrapper>
          }
        />

        {/* Voice Mail */}
        <Route
          path="/voice-mail"
          element={
            <TeamAccessWrapper moduleName="Voice Mail" showDebugToggle={false}>
              {({ selectedTeam, teams, clearTeamSelection, switchToTeam }) => (
                <DashboardLayout
                  selectedTeam={selectedTeam}
                  teams={teams}
                  clearTeamSelection={clearTeamSelection}
                  switchToTeam={switchToTeam}
                  isIframe={isIframe}
                >
                  <VoiceMail />
                </DashboardLayout>
              )}
            </TeamAccessWrapper>
          }
        />

        {/* Real-Time Voice */}
        <Route
          path="/realtime-voice"
          element={
            <TeamAccessWrapper
              moduleName="Real-Time Voice"
              showDebugToggle={false}
            >
              {({ selectedTeam, teams, clearTeamSelection, switchToTeam }) => (
                <DashboardLayout
                  selectedTeam={selectedTeam}
                  teams={teams}
                  clearTeamSelection={clearTeamSelection}
                  switchToTeam={switchToTeam}
                  isIframe={isIframe}
                >
                  <RealTimeVoice selectedTeam={selectedTeam} />
                </DashboardLayout>
              )}
            </TeamAccessWrapper>
          }
        />

        {/* Real-Time SMS */}
        <Route
          path="/realtime-sms"
          element={
            <TeamAccessWrapper
              moduleName="Real-Time SMS"
              showDebugToggle={false}
            >
              {({ selectedTeam, teams, clearTeamSelection, switchToTeam }) => (
                <DashboardLayout
                  selectedTeam={selectedTeam}
                  teams={teams}
                  clearTeamSelection={clearTeamSelection}
                  switchToTeam={switchToTeam}
                  isIframe={isIframe}
                >
                  <RealTimeSMS selectedTeam={selectedTeam} />
                </DashboardLayout>
              )}
            </TeamAccessWrapper>
          }
        />

        {/* Leads */}
        <Route
          path="/leads"
          element={
            <TeamAccessWrapper moduleName="Leads" showDebugToggle={false}>
              {({ selectedTeam, teams, clearTeamSelection, switchToTeam }) => (
                <DashboardLayout
                  selectedTeam={selectedTeam}
                  teams={teams}
                  clearTeamSelection={clearTeamSelection}
                  switchToTeam={switchToTeam}
                  isIframe={isIframe}
                >
                  <Leads teamId={selectedTeam.id} />
                </DashboardLayout>
              )}
            </TeamAccessWrapper>
          }
        />

        {/* Personas */}
        <Route
          path="/personas"
          element={
            <TeamAccessWrapper moduleName="Personas" showDebugToggle={false}>
              {({ selectedTeam, teams, clearTeamSelection, switchToTeam }) => (
                <DashboardLayout
                  selectedTeam={selectedTeam}
                  teams={teams}
                  clearTeamSelection={clearTeamSelection}
                  switchToTeam={switchToTeam}
                  isIframe={isIframe}
                >
                  <Personas />
                </DashboardLayout>
              )}
            </TeamAccessWrapper>
          }
        />

        {/* Companies */}
        <Route
          path="/companies"
          element={
            <TeamAccessWrapper moduleName="Companies" showDebugToggle={false}>
              {({ selectedTeam, teams, clearTeamSelection, switchToTeam }) => (
                <DashboardLayout
                  selectedTeam={selectedTeam}
                  teams={teams}
                  clearTeamSelection={clearTeamSelection}
                  switchToTeam={switchToTeam}
                  isIframe={isIframe}
                >
                  <Companies teamId={selectedTeam.id} />
                </DashboardLayout>
              )}
            </TeamAccessWrapper>
          }
        />

        {/* Research Viewer */}
        <Route
          path="/research"
          element={
            <TeamAccessWrapper
              moduleName="Research Viewer"
              showDebugToggle={false}
            >
              {({ selectedTeam, teams, clearTeamSelection, switchToTeam }) => (
                <DashboardLayout
                  selectedTeam={selectedTeam}
                  teams={teams}
                  clearTeamSelection={clearTeamSelection}
                  switchToTeam={switchToTeam}
                  isIframe={isIframe}
                >
                  <ResearchViewer teamId={selectedTeam.id} />
                </DashboardLayout>
              )}
            </TeamAccessWrapper>
          }
        />

        {/* Generators */}
        <Route
          path="/generators"
          element={
            <TeamAccessWrapper
              moduleName="Generator Sites"
              showDebugToggle={false}
            >
              {({ selectedTeam, teams, clearTeamSelection, switchToTeam }) => (
                <DashboardLayout
                  selectedTeam={selectedTeam}
                  teams={teams}
                  clearTeamSelection={clearTeamSelection}
                  switchToTeam={switchToTeam}
                  isIframe={isIframe}
                >
                  <Generators teamId={selectedTeam.id} />
                </DashboardLayout>
              )}
            </TeamAccessWrapper>
          }
        />

        {/* Collectors */}
        <Route
          path="/collectors"
          element={
            <TeamAccessWrapper
              moduleName="Facility Collectors"
              showDebugToggle={false}
            >
              {({ selectedTeam, teams, clearTeamSelection, switchToTeam }) => (
                <DashboardLayout
                  selectedTeam={selectedTeam}
                  teams={teams}
                  clearTeamSelection={clearTeamSelection}
                  switchToTeam={switchToTeam}
                  isIframe={isIframe}
                >
                  <Collectors teamId={selectedTeam.id} />
                </DashboardLayout>
              )}
            </TeamAccessWrapper>
          }
        />

        {/* Collector Research */}
        <Route
          path="/collector-research"
          element={
            <TeamAccessWrapper
              moduleName="Collector Research"
              showDebugToggle={false}
            >
              {({ selectedTeam, teams, clearTeamSelection, switchToTeam }) => (
                <DashboardLayout
                  selectedTeam={selectedTeam}
                  teams={teams}
                  clearTeamSelection={clearTeamSelection}
                  switchToTeam={switchToTeam}
                  isIframe={isIframe}
                >
                  <CollectorResearch teamId={selectedTeam.id} />
                </DashboardLayout>
              )}
            </TeamAccessWrapper>
          }
        />

        {/* Blacklist Checker */}
        <Route
          path="/blacklist-checker"
          element={
            <TeamAccessWrapper
              moduleName="Blacklist Checker"
              showDebugToggle={true}
              debugEnabled={debugEnabled}
              onDebugToggle={setDebugEnabled}
            >
              {({
                selectedTeam,
                teams,
                clearTeamSelection,
                switchToTeam,
                showDebugToggle,
                debugEnabled,
                onDebugToggle,
              }) => (
                <DashboardLayout
                  selectedTeam={selectedTeam}
                  teams={teams}
                  clearTeamSelection={clearTeamSelection}
                  switchToTeam={switchToTeam}
                  isIframe={isIframe}
                >
                  <BlacklistChecker
                    teamId={selectedTeam.id}
                    selectedTeam={selectedTeam}
                    teams={teams}
                    onTeamChange={clearTeamSelection}
                    debugEnabled={debugEnabled}
                    onDebugToggle={onDebugToggle}
                  />
                </DashboardLayout>
              )}
            </TeamAccessWrapper>
          }
        />

        {/* Generator Checker */}
        <Route
          path="/generator-checker"
          element={
            <TeamAccessWrapper
              moduleName="Generator Compliance Checker"
              showDebugToggle={true}
              debugEnabled={debugEnabled}
              onDebugToggle={setDebugEnabled}
            >
              {({
                selectedTeam,
                teams,
                clearTeamSelection,
                switchToTeam,
                showDebugToggle,
                debugEnabled,
                onDebugToggle,
              }) => (
                <DashboardLayout
                  selectedTeam={selectedTeam}
                  teams={teams}
                  clearTeamSelection={clearTeamSelection}
                  switchToTeam={switchToTeam}
                  isIframe={isIframe}
                >
                  <GeneratorChecker
                    teamId={selectedTeam.id}
                    selectedTeam={selectedTeam}
                    teams={teams}
                    onTeamChange={clearTeamSelection}
                    debugEnabled={debugEnabled}
                    onDebugToggle={onDebugToggle}
                  />
                </DashboardLayout>
              )}
            </TeamAccessWrapper>
          }
        />

        {/* Agent Orchestration */}
        <Route
          path="/agents"
          element={
            <TeamAccessWrapper moduleName="Agent Orchestration" showDebugToggle={false}>
              {({ selectedTeam, teams, clearTeamSelection, switchToTeam }) => (
                <DashboardLayout
                  selectedTeam={selectedTeam}
                  teams={teams}
                  clearTeamSelection={clearTeamSelection}
                  switchToTeam={switchToTeam}
                  isIframe={isIframe}
                >
                  <AgentOrchestration />
                </DashboardLayout>
              )}
            </TeamAccessWrapper>
          }
        />

        {/* Documentation */}
        <Route
          path="/docs"
          element={
            <TeamAccessWrapper moduleName="Documentation" showDebugToggle={false}>
              {({ selectedTeam, teams, clearTeamSelection, switchToTeam }) => (
                <DashboardLayout
                  selectedTeam={selectedTeam}
                  teams={teams}
                  clearTeamSelection={clearTeamSelection}
                  switchToTeam={switchToTeam}
                  isIframe={isIframe}
                >
                  <Documentation />
                </DashboardLayout>
              )}
            </TeamAccessWrapper>
          }
        />
        <Route
          path="/docs/:section"
          element={
            <TeamAccessWrapper moduleName="Documentation" showDebugToggle={false}>
              {({ selectedTeam, teams, clearTeamSelection, switchToTeam }) => (
                <DashboardLayout
                  selectedTeam={selectedTeam}
                  teams={teams}
                  clearTeamSelection={clearTeamSelection}
                  switchToTeam={switchToTeam}
                  isIframe={isIframe}
                >
                  <Documentation />
                </DashboardLayout>
              )}
            </TeamAccessWrapper>
          }
        />

        {/* Settings */}
        <Route
          path="/settings"
          element={
            <TeamAccessWrapper moduleName="Settings" showDebugToggle={false}>
              {({ selectedTeam, teams, clearTeamSelection, switchToTeam }) => (
                <DashboardLayout
                  selectedTeam={selectedTeam}
                  teams={teams}
                  clearTeamSelection={clearTeamSelection}
                  switchToTeam={switchToTeam}
                  isIframe={isIframe}
                >
                  <Settings />
                </DashboardLayout>
              )}
            </TeamAccessWrapper>
          }
        />

        {/* Catch all - redirect to main */}
        <Route
          path="*"
          element={<Navigate to={preserveQueryParams("/ai-sdr/")} replace />}
        />
      </Routes>
    </div>
  );
};

export default AiSdr;
